/**
 * Notification Store
 *
 * Pinia store managing real-time notification state for the application.
 * Works in tandem with useNotificationSSE composable for SSE delivery.
 *
 * Responsibilities:
 *  - Maintains in-memory notification list (last 50 items)
 *  - Tracks unread count (drives bell badge)
 *  - Manages CRITICAL alert queue (full-screen modal)
 *  - Provides actions for mark-read, mark-actioned, dismiss
 *  - Handles multi-tab sync via READ_RECEIPT / ACTIONED SSE events
 *
 * API Endpoints (tms-api-notifications service, via gateway prefix /notifications):
 *  - GET    /notifications/api/v1/notifications?user_id=&unread=true&limit=50
 *  - GET    /notifications/api/v1/notifications/unread-count?user_id=
 *  - GET    /notifications/api/v1/notifications/critical-unactioned?user_id=
 *  - PATCH  /notifications/api/v1/notifications/:id/read?user_id=
 *  - POST   /notifications/api/v1/notifications/read-all?user_id=
 *  - PATCH  /notifications/api/v1/notifications/:id/actioned?user_id=
 *  - DELETE /notifications/api/v1/notifications/:id?user_id=
 *  - GET    /notifications/api/v1/notifications/stream?user_id=
 *
 * Lifecycle:
 *  - SSE composable calls `handleIncoming()` for each NOTIFICATION event
 *  - SSE composable calls `handleReadReceipt()` for READ_RECEIPT events (multi-tab sync)
 *  - SSE composable calls `handleActioned()` for ACTIONED events (multi-tab sync)
 *  - On app load, `fetchInitialNotifications()` loads unread from REST API
 *  - On app load, `fetchCriticalUnactioned()` loads blocking alerts
 *  - Bell dropdown reads from `notifications` and `unreadCount`
 *  - CriticalAlertModal reads from `criticalQueue`
 */
import { defineStore } from 'pinia';
import type { AxiosInstance } from 'axios';
import type { SSENotificationPayload } from '@tms-phoenix/ui-vue';
import { createLogger } from '@tms-phoenix/ui-vue';
import { API_ROUTES } from '@/core/config';
import { logAuditEvent } from '@/core/services/audit.service';

const logger = createLogger('notification-store');

// ─── Types ──────────────────────────────────────────────────────────────────────

export type NotificationPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'CRITICAL';

export interface AppNotification {
  /** notification_recipients.recipient_id — primary key for API operations */
  notification_id: string;
  subject: string;
  rendered_body: string;
  priority: NotificationPriority;
  reference_entity_type?: string | null;
  reference_entity_id?: string | null;
  event_type?: string;
  read_at?: string | null;
  actioned_at?: string | null;
  created_at?: string;
}

/**
 * A recall that is in PENDING status (acknowledged by the user but not yet
 * Approved or Rejected). Persisted in the notification bell independently
 * of the read-state of the original notification.
 */
export interface PendingRecallItem {
  id: string;
  recallNumber: string;
  name: string;
  productName: string;
  recallDate: string;
  manufacturer?: string;
  affectedLotNumbers?: string;
  linkedProductId?: string;
  clinicalDescription?: string;
  informationSource?: string;
  sourceUrl?: string;
}

interface NotificationState {
  /** Last 50 notifications (drives bell dropdown inbox) */
  notifications: AppNotification[];
  /** Number of unread notifications (drives bell badge) */
  unreadCount: number;
  /** CRITICAL alerts waiting for full-screen modal acknowledgement */
  criticalQueue: AppNotification[];
  /** Whether the SSE stream is currently connected */
  isConnected: boolean;
  /** Whether initial notifications have been loaded from REST API */
  isInitialLoadComplete: boolean;
  /** Loading state for API calls */
  isLoading: boolean;
  /** Recalls that are in PENDING status (acknowledged but not yet actioned). */
  pendingRecalls: PendingRecallItem[];
  /** Recall notification IDs the user has dismissed for this session (backed by sessionStorage). */
  sessionDismissedRecallIds: string[];
}

// ─── Constants ──────────────────────────────────────────────────────────────────

const MAX_NOTIFICATIONS = 50;
const RECALL_BANNER_DISMISSED_KEY = 'recall-banner-dismissed';

/**
 * Read and validate the list of session-dismissed recall notification IDs from sessionStorage.
 * Returns an empty array if the stored value is missing, malformed JSON, or not a string array.
 */
function loadDismissedRecallIds(): string[] {
  try {
    const raw = sessionStorage.getItem(RECALL_BANNER_DISMISSED_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is string => typeof item === 'string');
  } catch {
    return [];
  }
}

/**
 * Base path for the notifications API.
 * Routes through the API gateway prefix `/notifications` → tms-api-notifications:5300
 */
const API_BASE = `${API_ROUTES.NOTIFICATIONS}/notifications`;

/**
 * Helper: reads user_id (staffId) from auth store in localStorage.
 * Avoids circular dependency with auth store by reading directly from persisted state.
 */
function getAuthContext(): { user_id: string } {
  try {
    const raw = localStorage.getItem('pinia-auth');
    if (!raw) return { user_id: '' };
    const parsed = JSON.parse(raw);
    return {
      user_id: parsed?.staffId ?? '',
    };
  } catch {
    return { user_id: '' };
  }
}

// ─── Helpers ────────────────────────────────────────────────────────────────────

/**
 * Unwrap API response data, handling both:
 *  - Unwrapped shape (createApiClient strips envelope → raw is the inner payload)
 *  - Envelope shape ({ success, data, meta } — defensive fallback)
 */
function unwrapRows(raw: unknown): Array<Record<string, unknown>> {
  if (Array.isArray(raw)) return raw;
  return (raw as { data?: Array<Record<string, unknown>> })?.data ?? [];
}

/**
 * Map a backend notification row (snake_case with recipient_id) to an AppNotification.
 * Centralises the mapping used by fetchInitialNotifications and fetchCriticalUnactioned.
 */
function mapRowToNotification(
  row: Record<string, unknown>,
  defaultPriority: NotificationPriority = 'NORMAL',
): AppNotification {
  return {
    notification_id: (row.recipient_id as string) ?? '',
    subject: (row.subject as string) ?? '',
    rendered_body: (row.rendered_body as string) ?? '',
    priority: (row.priority as NotificationPriority) ?? defaultPriority,
    reference_entity_type: (row.reference_entity_type as string | null) ?? null,
    reference_entity_id: (row.reference_entity_id as string | null) ?? null,
    event_type: (row.event_type as string) ?? undefined,
    read_at: (row.read_at as string | null) ?? null,
    actioned_at: (row.actioned_at as string | null) ?? null,
    created_at: (row.created_at as string) ?? new Date().toISOString(),
  };
}

// ─── Store ──────────────────────────────────────────────────────────────────────

export const useNotificationStore = defineStore('notifications', {
  state: (): NotificationState => ({
    notifications: [],
    unreadCount: 0,
    criticalQueue: [],
    isConnected: false,
    isInitialLoadComplete: false,
    isLoading: false,
    pendingRecalls: [],
    sessionDismissedRecallIds: loadDismissedRecallIds(),
  }),

  getters: {
    /**
     * Whether there are unread notifications to display on the bell badge.
     */
    hasUnread: (state): boolean => state.unreadCount > 0,

    /**
     * Whether there's a CRITICAL alert requiring immediate user action.
     */
    hasCriticalAlert: (state): boolean => state.criticalQueue.length > 0,

    /**
     * The first (oldest) CRITICAL alert in the queue.
     */
    currentCriticalAlert: (state): AppNotification | null =>
      state.criticalQueue[0] ?? null,

    /**
     * Number of pending CRITICAL alerts.
     */
    criticalAlertCount: (state): number => state.criticalQueue.length,

    /**
     * Unread notifications only.
     */
    unreadNotifications: (state: NotificationState): AppNotification[] =>
      state.notifications.filter((n: AppNotification) => !n.read_at),

    /**
     * Most recent unread recall notification (for top alert banner).
     * Returns the newest unread notification with reference_entity_type === 'RECALL'.
     * Used by RecallAlertBanner to show a persistent top-of-page alert.
     */
    latestRecallNotification: (state: NotificationState): AppNotification | null => {
      return (
        state.notifications.find(
          (n: AppNotification) =>
            !n.read_at &&
            (n.reference_entity_type === 'PRODUCT_RECALL' ||
              n.reference_entity_type === 'RECALL'),
        ) ?? null
      );
    },

    /**
     * Number of recalls awaiting hospital action (Approve/Reject).
     * Drives the notification bell badge alongside unread notification count.
     */
    pendingRecallCount: (state): number => state.pendingRecalls.length,

    /**
     * Whether there are any recalls awaiting hospital action.
     */
    hasPendingRecalls: (state): boolean => state.pendingRecalls.length > 0,

    /**
     * The first pending recall that is NOT session-dismissed for this session.
     * Driven by pendingRecalls (hospital.recall_status === 'PENDING') so the banner
     * stays visible until the recall is approved, rejected, or explicitly closed.
     * Acknowledging the critical alert notification does NOT hide the banner.
     */
    latestVisibleRecallAlert: (state: NotificationState): PendingRecallItem | null =>
      state.pendingRecalls.find(
        (r: PendingRecallItem) => !state.sessionDismissedRecallIds.includes(r.id),
      ) ?? null,

    /**
     * Count of pending recalls that are not session-dismissed.
     */
    visibleRecallCount: (state: NotificationState): number =>
      state.pendingRecalls.filter(
        (r: PendingRecallItem) => !state.sessionDismissedRecallIds.includes(r.id),
      ).length,

    /**
     * Whether the recall alert banner should be shown.
     * True when at least one pending, non-session-dismissed recall exists.
     * Used by AppHeader (for compact height) and AppLayout (for breadcrumb padding).
     */
    isRecallBannerVisible(): boolean {
      return this.latestVisibleRecallAlert !== null;
    },
  },

  actions: {
    // ── SSE Event Handlers ──────────────────────────────────────────────────

    /**
     * Called by useNotificationSSE `onNotification` callback.
     * Routes incoming notification to the appropriate queue based on priority.
     *
     * Maps BE field `recipient_id` → internal `notification_id`.
     */
    handleIncoming(payload: SSENotificationPayload) {
      const notification: AppNotification = {
        notification_id: payload.recipient_id,
        subject: payload.subject,
        rendered_body: payload.rendered_body,
        priority: payload.priority,
        reference_entity_type: payload.reference_entity_type,
        reference_entity_id: payload.reference_entity_id,
        event_type: payload.event_type,
        read_at: null,
        actioned_at: null,
        created_at: payload.created_at ?? new Date().toISOString(),
      };

      // Deduplicate — don't add if already in list
      const exists = this.notifications.some(
        (n: AppNotification) => n.notification_id === notification.notification_id,
      );
      if (exists) return;

      // Add to notification list (most recent first, cap at MAX)
      this.notifications = [notification, ...this.notifications].slice(0, MAX_NOTIFICATIONS);
      this.unreadCount++;

      // Route based on priority
      if (notification.priority === 'CRITICAL') {
        // CRITICAL → full-screen blocking modal (no toast)
        this.criticalQueue.push(notification);
      }
      // HIGH/NORMAL/LOW: visible in drawer only — no toast, no modal.
    },

    /**
     * Handle READ_RECEIPT SSE event — another tab marked notification(s) as read.
     * Updates local state to stay in sync without re-fetching.
     *
     * @param recipientId - specific recipient_id, or null for "all cleared" (markAllRead)
     */
    handleReadReceipt(recipientId: string | null) {
      if (recipientId === null) {
        // "All cleared" — mark every notification as read locally
        const now = new Date().toISOString();
        this.notifications = this.notifications.map((n: AppNotification) =>
          !n.read_at ? { ...n, read_at: now } : n,
        );
        this.unreadCount = 0;
        return;
      }

      const notification = this.notifications.find(
        (n: AppNotification) => n.notification_id === recipientId,
      );
      if (notification && !notification.read_at) {
        notification.read_at = new Date().toISOString();
        this.unreadCount = Math.max(0, this.unreadCount - 1);
      }
    },

    /**
     * Handle ACTIONED SSE event — another tab acknowledged a CRITICAL alert.
     * Removes from critical queue and marks as actioned locally.
     */
    handleActioned(recipientId: string) {
      // Remove from critical queue
      this.criticalQueue = this.criticalQueue.filter(
        (n: AppNotification) => n.notification_id !== recipientId,
      );

      // Update in notifications list
      const notification = this.notifications.find(
        (n: AppNotification) => n.notification_id === recipientId,
      );
      if (notification) {
        const now = new Date().toISOString();
        if (!notification.read_at) {
          notification.read_at = now;
          this.unreadCount = Math.max(0, this.unreadCount - 1);
        }
        notification.actioned_at = now;
      }
    },

    /**
     * Update SSE connection state.
     */
    setConnectionState(connected: boolean) {
      this.isConnected = connected;
    },

    // ── REST API Actions ────────────────────────────────────────────────────

    /**
     * Fetch initial unread notifications from REST API on app load / login.
     * Called once after SSE connection is established.
     * Populates the bell dropdown inbox with the most recent unread items.
     *
     * Maps BE `recipient_id` → internal `notification_id` for each row.
     */
    async fetchInitialNotifications(apiClient: Pick<AxiosInstance, 'get'>): Promise<boolean> {
      if (this.isInitialLoadComplete) return true;

      const { user_id } = getAuthContext();
      if (!user_id) {
        logger.debug('Missing auth context — skipping fetch');
        return true;
      }

      this.isLoading = true;
      try {
        const response = await apiClient.get(API_BASE, {
          params: { user_id, unread: 'true', limit: String(MAX_NOTIFICATIONS) },
        });

        // createApiClient unwraps ApiResponse, so response.data is already the
        // inner payload (array of rows). Handle both unwrapped and envelope shapes
        // for defensive compatibility.
        const raw = response.data;
        const rows = unwrapRows(raw);

        if (rows.length > 0) {
          this.notifications = rows.map((row) => mapRowToNotification(row));
        }

        // Derive unread count: prefer meta.count if envelope is intact,
        // otherwise count rows without read_at.
        const meta = (raw as { meta?: { count?: number } })?.meta;
        this.unreadCount = meta?.count ?? rows.filter((n) => !n.read_at).length;

        this.isInitialLoadComplete = true;
        return true;
      } catch {
        logger.warn('Failed to fetch initial notifications');
        return false;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Fetch un-actioned CRITICAL notifications from dedicated endpoint.
     * Called on login to populate the blocking modal queue.
     * Uses GET /notifications/api/v1/notifications/critical-unactioned
     */
    async fetchCriticalUnactioned(apiClient: Pick<AxiosInstance, 'get'>): Promise<boolean> {
      const { user_id } = getAuthContext();
      if (!user_id) {
        logger.warn('Missing auth context — skipping fetch');
        return true;
      }

      try {
        const response = await apiClient.get(`${API_BASE}/critical-unactioned`, {
          params: { user_id },
        });

        // createApiClient unwraps ApiResponse, so response.data is already the
        // inner payload (array of rows). Handle both shapes defensively.
        const rows = unwrapRows(response.data);

        if (rows.length > 0) {
          // Map and deduplicate against existing criticalQueue
          const mapped = rows.map((row) => mapRowToNotification(row, 'CRITICAL'));

          const existingIds = new Set(this.criticalQueue.map((n: AppNotification) => n.notification_id));
          const newCritical = mapped.filter(
            (n: AppNotification) => !existingIds.has(n.notification_id),
          );
          this.criticalQueue = [...this.criticalQueue, ...newCritical];
        }
        return true;
      } catch {
        logger.warn('Failed to fetch critical notifications');
        return false;
      }
    },

    /**
     * Mark a notification as read.
     * Calls PATCH /notifications/api/v1/notifications/:id/read and updates local state.
     * The backend broadcasts READ_RECEIPT to other tabs via SSE.
     */
    async markAsRead(
      notificationId: string,
      apiClient: Pick<AxiosInstance, 'patch'>,
    ): Promise<boolean> {
      const { user_id } = getAuthContext();
      if (!user_id) {
        logger.warn('Missing auth context — skipping action');
        return true;
      }

      try {
        await apiClient.patch(`${API_BASE}/${encodeURIComponent(notificationId)}/read`, {}, {
          params: { user_id },
        });

        // Update local state
        const notification = this.notifications.find(
          (n: AppNotification) => n.notification_id === notificationId,
        );
        if (notification && !notification.read_at) {
          notification.read_at = new Date().toISOString();
          this.unreadCount = Math.max(0, this.unreadCount - 1);
        }
        return true;
      } catch {
        logger.warn('Failed to mark notification as read');
        return false;
      }
    },

    /**
     * Mark all notifications as read using the batch endpoint.
     * Calls POST /notifications/api/v1/notifications/read-all
     * The backend broadcasts READ_RECEIPT for each to other tabs.
     */
    async markAllAsRead(apiClient: Pick<AxiosInstance, 'post'>) {
      const { user_id } = getAuthContext();
      if (!user_id) {
        logger.warn('Missing auth context — skipping action');
        return;
      }

      // Snapshot current state for rollback
      const prevNotifications = this.notifications;
      const prevUnreadCount = this.unreadCount;

      const now = new Date().toISOString();

      // Optimistic update
      this.notifications = this.notifications.map((n: AppNotification) =>
        !n.read_at ? { ...n, read_at: now } : n,
      );
      this.unreadCount = 0;

      try {
        await apiClient.post(`${API_BASE}/read-all`, {}, {
          params: { user_id },
        });
      } catch (err) {
        logger.warn('Failed to mark all notifications as read');

        // Rollback optimistic update
        this.notifications = prevNotifications;
        this.unreadCount = prevUnreadCount;

        // Re-throw so the caller can surface an error to the user
        throw err;
      }
    },

    /**
     * Dismiss a CRITICAL alert after user acknowledges it.
     * Calls PATCH /notifications/api/v1/notifications/:id/actioned and removes from queue.
     * The backend broadcasts ACTIONED to other tabs via SSE.
     *
     * Only mutates local state after a successful backend response so the modal
     * remains blocking until acknowledgement is persisted.
     */
    async dismissCriticalAlert(
      notificationId: string,
      apiClient: Pick<AxiosInstance, 'patch'>,
    ) {
      const { user_id } = getAuthContext();
      if (!user_id) {
        throw new Error('[NotificationStore] Missing auth context — cannot dismiss critical alert');
      }

      // Capture snapshot before the API call for audit
      const queuedNotification = this.criticalQueue.find(
        (n: AppNotification) => n.notification_id === notificationId,
      );
      const isRecallNotification = queuedNotification?.reference_entity_type === 'PRODUCT_RECALL';

      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;

      try {
        // Persist acknowledgement first — keep the modal blocking on failure
        await apiClient.patch(`${API_BASE}/${encodeURIComponent(notificationId)}/actioned`, {}, {
          params: { user_id },
        });
        auditStatus = 'SUCCESS';
      } catch (err) {
        auditStatus = 'FAILURE';
        auditErrorCode = 'ACKNOWLEDGE_RECALL_NOTIFICATION_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : String(err);
        throw err;
      } finally {
        if (isRecallNotification) {
          try {
            logAuditEvent({
              eventCategory: 'RECALL_MANAGEMENT',
              eventType: 'ACKNOWLEDGE_NOTIFICATION',
              eventStatus: auditStatus,
              resourceType: 'notifications',
              resourceId: notificationId,
              isPhiAccess: false,
              oldValue: { actioned_at: queuedNotification?.actioned_at ?? null },
              newValue: {
                actioned_at: new Date().toISOString(),
                recall_id: queuedNotification?.reference_entity_id ?? undefined,
                user_id,
              },
              errorCode: auditErrorCode,
              errorMessage: auditErrorMessage,
            });
          } catch {
            // Audit must never block the flow
          }
        }
      }

      // Only remove from critical queue after successful response
      this.criticalQueue = this.criticalQueue.filter(
        (n: AppNotification) => n.notification_id !== notificationId,
      );

      // Also mark as read + actioned locally
      const notification = this.notifications.find(
        (n: AppNotification) => n.notification_id === notificationId,
      );
      if (notification) {
        const now = new Date().toISOString();
        if (!notification.read_at) {
          notification.read_at = now;
          this.unreadCount = Math.max(0, this.unreadCount - 1);
        }
        notification.actioned_at = now;
      }
    },

    // ── Utility ─────────────────────────────────────────────────────────────

    /**
     * Fetch the lightweight unread count from the dedicated badge endpoint.
     * Uses GET /notifications/api/v1/notifications/unread-count
     */
    async fetchUnreadCount(apiClient: Pick<AxiosInstance, 'get'>): Promise<boolean> {
      const { user_id } = getAuthContext();
      if (!user_id) {
        logger.warn('Missing auth context — skipping fetch');
        return true;
      }

      try {
        const response = await apiClient.get(`${API_BASE}/unread-count`, {
          params: { user_id },
        });

        // createApiClient unwraps ApiResponse, so response.data is already
        // the inner payload ({ count: number }). Handle both shapes defensively.
        const raw = response.data as { count?: number; data?: { count: number } };
        const count = raw?.count ?? raw?.data?.count;

        if (typeof count === 'number') {
          this.unreadCount = count;
        }
        return true;
      } catch {
        logger.warn('Failed to fetch unread count');
        return false;
      }
    },

    /**
     * Fetch missed unread notifications and merge them into the existing store state.
     *
     * Called on SSE reconnect when the unread count has changed since disconnect,
     * indicating events arrived while the stream was down.  De-duplicates using
     * notification_id so existing items are never duplicated in the list.
     *
     * Cost: 1 GET per reconnect event where count actually changed (rare).
     */
    async fetchMissedNotifications(apiClient: Pick<AxiosInstance, 'get'>): Promise<boolean> {
      const { user_id } = getAuthContext();
      if (!user_id) {
        logger.debug('Missing auth context — skipping fetch');
        return true;
      }

      try {
        const response = await apiClient.get(API_BASE, {
          params: { user_id, unread: 'true', limit: String(MAX_NOTIFICATIONS) },
        });

        const rows = unwrapRows(response.data);
        logger.debug('Notification catch-up: REST response received', { rowCount: rows.length });
        if (!rows.length) return true;

        // De-duplicate: only add items not already in the local store
        const existingIds = new Set(this.notifications.map((n: AppNotification) => n.notification_id));
        const incoming = rows
          .map((row) => mapRowToNotification(row))
          .filter((n: AppNotification) => !existingIds.has(n.notification_id));

        if (incoming.length > 0) {
          // Prepend newest items — server returns newest first
          this.notifications = [...incoming, ...this.notifications].slice(0, MAX_NOTIFICATIONS);
        }

        // Re-sync unread count from server source of truth
        const meta = (response.data as { meta?: { count?: number } })?.meta;
        logger.debug('Notification catch-up: merge complete', { newItemsMerged: incoming.length });
        this.unreadCount = meta?.count ?? rows.filter((n) => !n.read_at).length;
        return true;
      } catch {
        logger.warn('Failed to fetch missed notifications');
        return false;
      }
    },

    /**
     * Permanently dismiss (delete) a notification from the user inbox.
     * Calls DELETE /notifications/api/v1/notifications/:id
     */
    async dismissNotification(
      notificationId: string,
      apiClient: Pick<AxiosInstance, 'delete'>,
    ) {
      const { user_id } = getAuthContext();
      if (!user_id) {
        logger.warn('Missing auth context — skipping action');
        return;
      }

      try {
        await apiClient.delete(`${API_BASE}/${encodeURIComponent(notificationId)}`, {
          params: { user_id },
        });

        // Remove from local state
        const removed = this.notifications.find(
          (n: AppNotification) => n.notification_id === notificationId,
        );
        this.notifications = this.notifications.filter(
          (n: AppNotification) => n.notification_id !== notificationId,
        );
        if (removed && !removed.read_at) {
          this.unreadCount = Math.max(0, this.unreadCount - 1);
        }
      } catch {
        logger.warn('Failed to dismiss notification');
      }
    },

    /**
     * Fetch recalls currently in PENDING status.
     * Called on initial SSE connect and whenever a new PRODUCT_RECALL notification
     * arrives. Results populate the dedicated "Pending Recalls" section of the
     * notification bell so they remain visible even after the notification is read.
     *
     * Uses GET {RECALL}/recalls?limit=100 — no server-side status
     * filter, because 'PENDING' is a hospital-response status on
     * item.hospital.recall_status, not the recall's own DynamoDB status field
     * (which only accepts 'ACTIVE' | 'CANCELLED'). Filtering is done client-side.
     */
    async fetchPendingRecalls(apiClient: Pick<AxiosInstance, 'get'>): Promise<boolean> {
      try {
        // Fetch all active recalls (no status filter — 'PENDING' is a
        // hospital-response status on item.hospital.recall_status, NOT the recall's own
        // DynamoDB status field which only accepts 'ACTIVE' | 'CANCELLED').
        const response = await apiClient.get(`${API_ROUTES.RECALL}/recalls`, {
          params: { limit: '100' },
        });
        const raw = response.data;
        // Handle envelope { data: { items } }, { items }, or raw array
        const data = (raw as { data?: { items?: unknown[] } })?.data;
        const items: unknown[] = (
          data?.items
          ?? (raw as { items?: unknown[] })?.items
          ?? (Array.isArray(raw) ? raw : [])
        ) as unknown[];

        // Keep only recalls where this hospital's response is still PENDING
        // (acknowledged — notification was delivered — but no Approve/Reject action taken yet).
        const pendingItems = (items as Array<Record<string, unknown>>).filter((item) => {
          const hospital = item.hospital as { recall_status?: string } | null | undefined;
          return hospital?.recall_status === 'PENDING';
        });

        this.pendingRecalls = pendingItems.map((item) => ({
          id: (item.recall_id as string) ?? '',
          recallNumber: (item.recall_reference as string) ?? '',
          name: (item.recall_title as string) ?? '',
          productName: (Array.isArray(item.linked_products)
            ? (item.linked_products as string[]).join(', ')
            : '') || '—',
          recallDate: (item.recall_date as string) ?? '—',
          manufacturer: (item.affected_manufacturer as string | undefined) ?? undefined,
          affectedLotNumbers: (Array.isArray(item.affected_batch_numbers)
            ? (item.affected_batch_numbers as string[]).join(', ')
            : '') || undefined,
          linkedProductId: Array.isArray(item.product_id)
            ? (item.product_id as string[])[0]
            : undefined,
          clinicalDescription: (item.reason as string | undefined) ?? undefined,
          informationSource: (item.source_origin as string | undefined) ?? undefined,
          sourceUrl: (item.source_url as string | undefined) ?? undefined,
        }));
        return true;
      } catch {
        logger.warn('Failed to fetch pending recalls');
        return false;
      }
    },

    /**
     * Session-dismiss a recall banner by recall ID.
     * Adds the recall ID to the in-memory dismissed list AND persists to sessionStorage
     * so it survives page navigation within the session. The list is cleared on logout ($reset),
     * so the banner reappears after login for any still-pending recall.
     */
    dismissRecallNotification(recallId: string) {
      if (!recallId) return;
      if (this.sessionDismissedRecallIds.includes(recallId)) return;
      this.sessionDismissedRecallIds = [...this.sessionDismissedRecallIds, recallId];
      try {
        sessionStorage.setItem(
          RECALL_BANNER_DISMISSED_KEY,
          JSON.stringify(this.sessionDismissedRecallIds),
        );
      } catch {
        // sessionStorage may be unavailable in some environments
      }
    },

    /**
     * Remove a recall from the pending recalls list after it has been
     * Approved or Rejected via the notification bell modal.
     */
    removePendingRecall(recallId: string) {
      this.pendingRecalls = this.pendingRecalls.filter((r: PendingRecallItem) => r.id !== recallId);
    },

    /**
     * Clear all notification state (called on logout).
     * Also clears the session-dismissed recall banner IDs so the banner
     * reappears when the user logs in again.
     */
    $reset() {
      this.notifications = [];
      this.unreadCount = 0;
      this.criticalQueue = [];
      this.isConnected = false;
      this.isInitialLoadComplete = false;
      this.isLoading = false;
      this.pendingRecalls = [];
      this.sessionDismissedRecallIds = [];
      try {
        sessionStorage.removeItem(RECALL_BANNER_DISMISSED_KEY);
      } catch {
        // sessionStorage may be unavailable in some environments
      }
    },
  },
});
